import axios from 'axios';

export type AuthErrorKind =
  | 'network'
  | 'notFound'
  | 'wrongPassword'
  | 'emailTaken'
  | 'phoneTaken'
  | 'badCode'
  | 'tooMany'
  | 'unknown';

function serverMessages(error: unknown): string[] {
  if (!axios.isAxiosError(error)) return [];
  const raw = (error.response?.data as { message?: unknown } | undefined)?.message;
  if (Array.isArray(raw)) return raw.map((m) => (typeof m === 'string' ? m : String((m as { message?: string })?.message ?? '')));
  return typeof raw === 'string' ? [raw] : [];
}

/**
 * Turns an API error into one of a few cases the UI explains in the user's
 * language — the server's own messages are English and technical.
 */
export function authErrorKind(error: unknown): AuthErrorKind {
  if (!axios.isAxiosError(error)) return 'unknown';
  if (!error.response) return 'network';
  const status = error.response.status;
  const text = serverMessages(error).join(' ').toLowerCase();
  const taken = /\b(already|exists?|taken|in use)\b/.test(text);
  if (status === 429) {
    // 429 AUTH_RATE_LIMITED { retryAfter } (sign-in, forgot / reset password): say how long.
    const after = Number((error.response.data as { retryAfter?: unknown } | undefined)?.retryAfter);
    lastRetryMinutes = Math.max(1, Math.ceil((Number.isFinite(after) && after > 0 ? after : 60) / 60));
    return 'tooMany';
  }
  if (status === 404 || /not found/.test(text)) return 'notFound';
  if (/\bcode\b/.test(text) && /\b(invalid|expired|wrong|incorrect)\b/.test(text)) return 'badCode';
  if (/phone/.test(text) && taken) return 'phoneTaken';
  if (taken || status === 409) return 'emailTaken';
  if (status === 401 || status === 403 || /(password|credential)/.test(text)) return 'wrongPassword';
  if (status >= 500) return 'network';
  return 'unknown';
}

export const authErrorKey = (kind: AuthErrorKind): string => `authFlow.errors.${kind}`;

/** Minutes to wait from the last 429 seen (the screens keep only the kind). */
let lastRetryMinutes = 1;

/** The message for a kind — «tooMany» with the server's wait time. */
export function authErrorText(t: (key: string, opts?: Record<string, unknown>) => string, kind: AuthErrorKind, fallback: string): string {
  return t(authErrorKey(kind), { min: lastRetryMinutes, defaultValue: fallback });
}
